import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { BlockDisplayElementChangedEvent } from '../models/block-display-element-changed-event.model';
import { GridsterConfig } from 'angular-gridster2';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { BlockModel } from '../models/block.model';
import { BlockMoveDirectionEnum } from '../enums/direction.enum';
import { BlockTypeGroupModel } from '../models/block-type-group.model';
import { BlockTypeModel } from '../models/block-type.model';
import { BriefingBlockTypeEnum } from '../../../Monitor/Briefings/enums/briefing-block-type.enum';
import { briefingBlockTypeToGeneralStylesPropertyMap } from '../../../Monitor/Briefings/models/briefing-global-styles-property.const';
import { DisplayElementModel } from '../models/display-element.model';
import { GenericStyleModel } from '../../Styles/models/generic-style.model';
import { Style } from '../../Styles/models/style.model';
import { StylesService } from '../../Styles/services/styles.service';
import { StyleStateEnum } from '../enums/style-state.enum';
import { Template } from '../../Styles/models/template.model';
import { TwigRendererService } from './twig-renderer.service';
import { BlockValidationTypeEnum } from '../enums/block-validation-type.enum';
import { FocusedBlockInfo } from '../models/focused-block-info.model';
import { LayoutBlock } from '../models/layout-block.model';
import { CuratorBlock } from '../models/curator-block.model';
import { DateQueryModel } from '../../Forms/models/date-query.model';

export abstract class AbstractPageBuilderService {
    blocks: BlockModel[];
    blocksProvider: BehaviorSubject<BlockModel[]>;

    blockTypes: BlockTypeModel[];
    blockTypesProvider: BehaviorSubject<BlockTypeModel[]>;

    blockTypeGroups: BlockTypeGroupModel[];
    blockTypeGroupsProvider: BehaviorSubject<BlockTypeGroupModel[]>;

    layoutBlockTypes: BlockTypeModel[];
    layoutBlockTypes$: BehaviorSubject<BlockTypeModel[]> = new BehaviorSubject<BlockTypeModel[]>(null);

    protected draggingBlock: BlockModel = null;
    protected draggingBlock$: BehaviorSubject<BlockModel> = new BehaviorSubject<BlockModel>(null);
    protected draggingBlockType: BlockTypeModel = null;
    protected draggingBlockType$: BehaviorSubject<BlockTypeModel> = new BehaviorSubject<BlockTypeModel>(null);
    protected draggingBlockPosition: string = null;
    protected draggingBlockPosition$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    protected focusedBlockInfo: FocusedBlockInfo;
    protected focusedBlockInfo$: BehaviorSubject<FocusedBlockInfo> = new BehaviorSubject<FocusedBlockInfo>(null);
    protected textBlockToolbarFixedTop: number;
    protected textBlockToolbarFixedTop$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    protected displayChartEntityProfile: boolean = false;
    protected displaySourceAsLink: boolean = true;
    protected incrementRefreshAllProgressBar$: BehaviorSubject<BlockModel> = new BehaviorSubject(null);

    selectedBlock: BlockModel;
    selectedBlockOriginalValues: DisplayElementModel;
    selectedBlockProvider: BehaviorSubject<BlockModel>;

    selectedBlockUpdatedProvider: Subject<void>;

    generalStyles: {};
    generalStylesProvider: BehaviorSubject<any>;

    public generalStylesToBlocksMap: Map<string, Map<string, string>>;
    public blockToGeneralStylePropertiesMap: Map<string, Map<string, string>>;

    /**
     * Defines whether a page builder should be a grid
     *
     * @note Do not change this property at runtime. This should be a readonly property set on each pagebuilder
     */
    readonly isGrid: boolean = false;

    /**
     * Configurations for when the page builder is a grid
     *
     * @url https://github.com/tiberiuzuld/angular-gridster2/blob/5.x/src/lib/gridsterConfig.constant.ts
     */
    gridSettings: GridsterConfig;

    protected subscriptions: Subscription[] = [];
    protected styles: Style[];

    // key format is blockType+defaultReference
    protected defaultTemplates: Map<string, string> =  new Map();
    protected hasDefaultTemplates$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public defaultStyles: {[key: string]: any};
    public isContextMenuOutside: boolean = false;
    public useContextMenuForMove: boolean = false;

    private styleApiEnumToBlockTypeEnumMap: Map<string, string> = new Map();
    protected blockEditingState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected blockMaxWidth$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    protected inlineAdderHeight$: BehaviorSubject<number> = new BehaviorSubject<number>(274);

    protected cachedSelectedDate: DateQueryModel = null;
    /**
     * Subject that emits when block display element properties have changed.
     * Block components subscribe to this instead of using ngDoCheck.
     */
    protected blockDisplayElementChanged$: Subject<BlockDisplayElementChangedEvent> = new Subject<BlockDisplayElementChangedEvent>();

    constructor(
        public stylesService: StylesService,
        public twigRenderService: TwigRendererService,

        // HACK: localizationService is not used in AbstractPageBuilderService
        // but breaks in DistributionService cacheTemplates function where localizationService is not defined
        // (which it is) weird issue, can't 100% explain,
        // but seems like on the odd occasion super() is not smart enough know the child parameters
        public localizationService: LocalizationService,
    ) {
        this.blocks = [];
        this.blocksProvider = new BehaviorSubject<BlockModel[]>(this.blocks);
        this.blockTypes = null;
        this.blockTypesProvider = new BehaviorSubject<BlockTypeModel[]>(this.blockTypes);
        this.blockTypeGroups = null;
        this.blockTypeGroupsProvider = new BehaviorSubject<BlockTypeGroupModel[]>(this.blockTypeGroups);
        this.selectedBlock = null;
        this.selectedBlockProvider = new BehaviorSubject<BlockModel>(this.selectedBlock);
        this.selectedBlockUpdatedProvider = new Subject<void>();
        this.generalStyles = {};
        this.generalStylesProvider = new BehaviorSubject<any>(this.generalStyles);

        this.subscriptions.push(
            this.stylesService.all$.pipe(
                filter(x => !!x)
            ).subscribe((styles) => {
                this.styles = styles;
                if (this.blocks) {
                    this.setTemplatesOnBlocks(this.blocks);
                }
            }),
            this.stylesService.refreshTemplates().pipe(
                filter(x => !!x)
            ).subscribe(templates => this.cacheTemplates(templates))
        );

        // classes that extend this should set their list of block types and optionally block type groups
    }

    getFocusedResource(): Observable<any> {
        // override depending on the service
        return of(null);
    }

    /**
     * Sets what blocks can be moved up and down
     */
    protected updateMoveability(): void {
        if (this.blocks.length === 0) {
            return;
        }
        for (let i = 0, l = this.blocks.length; i < l; i++) {
            this.blocks[i].canMoveUp = true;
            this.blocks[i].canMoveDown = true;
        }
        this.blocks[0].canMoveUp = false;
        this.blocks[this.blocks.length - 1].canMoveDown = false;
    }

    /**
     * Gets an observable of the availble block types
     *
     * @returns An observable list of the currently available block types
     */
    getBlockTypes(): Observable<BlockTypeModel[]> {
        return this.blockTypesProvider;
    }

    /**
     * Sets the block types that are available to be added to the page
     */
    protected setBlockTypes(blockTypes: BlockTypeModel[]): void {
        this.blockTypes = blockTypes;
        this.blockTypesProvider.next(this.blockTypes);
        this.updateBlockTypeStatuses();
    }

    /**
     * Gets an observable of the availble block types
     *
     * @returns An observable list of the currently available block types
     */
    getBlockTypeGroups(): Observable<BlockTypeGroupModel[]> {
        return this.blockTypeGroupsProvider;
    }

    /**
     * Sets the groups of block types for rendering the block adder
     * NOTE: if block type groups are not set, then the adder will just render the base list of block types instead
     */
    protected setBlockTypeGroups(blockTypeGroups: BlockTypeGroupModel[]): void {
        this.blockTypeGroups = blockTypeGroups;
        this.blockTypeGroupsProvider.next(this.blockTypeGroups);
    }

    protected setLayoutBlockTypes(layoutBlockTypes: BlockTypeModel[]): void {
        this.layoutBlockTypes = layoutBlockTypes;
        this.layoutBlockTypes$.next(this.layoutBlockTypes);
    }

    getLayoutBlockTypes(): Observable<BlockTypeModel[]> {
        return this.layoutBlockTypes$;
    }

    protected updateBlockTypeStatuses(): void {
        // override this if any block types need to be limited
    }

    /**
     * Gets an observable of the blocks that are currently available
     *
     * @returns An observable list of blocks
     */
    getBlocks(): Observable<BlockModel[]> {
        return this.blocksProvider;
    }

    /**
     * Updates the list of blocks
     *
     * @param blocks A list of blocks
     */
    setBlocks(blocks: BlockModel[]): void {
        this.blocks = blocks;
        this.updateMoveability();
        this.updateBlockTypeStatuses();
        this.blocksProvider.next(this.blocks);
    }

    /**
     * Adds a block to the current list
     *
     * @param block A block object
     */
    addBlock(block: BlockModel): void {
        if (this.blockExists(block)) {
            // already in the list
            return;
        }
        block.id = Math.max(...this.blocks.map(x => x.id), 0) + 1;
        this.applyGeneralStylesToBlock(block);

        if (!block.type) {
            throw new Error('Blocks must have a type');
        }

        this.blocks.push(block);
        this.setBlocks(this.blocks);
    }

    /**
     * function that does the same thing as addBlockOfType, but we want to pass the event as part of the params
     * so we can see where it was clicked from on the screen
     * event is being ignored, cause it's only used in briefings service
     */
    addBlockOfTypeWithEvent(type: BlockTypeModel, position: string, event: Event): void {
        this.addBlockOfType(type, position);
    }

    /**
     * Adds a block to the current list
     *
     * @param type A block type
     */
    addBlockOfType(type: BlockTypeModel, position?: string): void {
        const positionToAdd = position ? +position : null;
        const block = new BlockModel(null, type, new type.displayElementType());
        if ((!!positionToAdd || positionToAdd === 0 ) && positionToAdd >= 0 && positionToAdd < this.blocks.length) {
            this.blocks.splice(positionToAdd, 0, block);
        } else {
            this.blocks.push(block);
        }

        this.setBlocks(this.blocks);
    }

    /**
     * Adds a block to the current list
     *
     * @param type A block type
     */
    addBlockAtPosition(block: BlockModel, position: string): void {
        if (!isNaN(+position)) {
            const positionToAdd = +position;
            if ((!!positionToAdd || positionToAdd === 0 ) && positionToAdd >= 0 && positionToAdd < this.blocks.length) {
                this.blocks.splice(positionToAdd, 0, block);
            } else {
                this.blocks.push(block);
            }
        } else {
            const indexParts = position.split('.');
            const blockPosition = +indexParts[0];
            const columnIndex = +indexParts[1];
            const positionInColumn = +indexParts[2];
            const layoutBlock = this.blocks[blockPosition] as LayoutBlock;
            const column = layoutBlock.blocks[columnIndex];
            const index = positionInColumn;
            column.splice(index, 0, block);
        }

        this.setBlocks(this.blocks);

        if (block.displayElement.styles.displayStyleState !== StyleStateEnum.CUSTOM) {
            this.applyGeneralStylesToBlock(block);
        }
    }

    /**
     * Removes a block from the current list
     *
     * @param block The block to remove
     */
    removeBlock(block: BlockModel, position?: string): void {
        const index = this.blocks.indexOf(block);

        if (index < 0) {
            // shouldn't ever happen
            throw new Error('Item not in list');
        }

        // If the deleted block is the selected block, unselect it
        if (this.selectedBlock && this.selectedBlock.displayElement?.isEqual(block.displayElement)) {
            this.deselectBlock(false);
        }
        this.blocks.splice(index, 1);
        this.setBlocks(this.blocks);
    }

    /**
     * Duplicate a block
     *
     * @param block The block to duplicate
     */
    duplicateBlock(block: BlockModel, position?: string): void {
        const index = this.blocks.indexOf(block);

        if (index < 0) {
            // shouldn't ever happen
            throw new Error('Item not in list');
        }

        this.blocks.splice(index, 0, block.clone());
        this.setBlocks(this.blocks);
    }


    /**
     * Selects a block for editing
     * @param block A Block from the current PageBuilder
     */
    selectBlock(block: BlockModel): void {
        this.selectedBlock = block;
        try {
            if (block?.displayElement) {
                this.selectedBlockOriginalValues = block.displayElement.clone();
            }
        } catch (e) {
            // catch data provider error
        } finally {
            this.selectedBlockProvider.next(this.selectedBlock);
        }
    }

    /**
     * Gets the currently selected block. `null` if no block is selected
     *
     * @returns An observable of the currently selected block
     */
    getSelectedBlock(): Observable<BlockModel> {
        return this.selectedBlockProvider;
    }

    /**
     * Updates the block form component with updates to the selected block
     */
    selectedBlockUpdated(): void {
        this.selectedBlockUpdatedProvider.next();
        this.notifyBlockDisplayElementChanged(this.selectedBlock);
    }

    /**
     * Returns the update selected block provider
     */
    getSelectedBlockUpdated(): Observable<void> {
        return this.selectedBlockUpdatedProvider;
    }

    /**
     * Returns general styles provider
     */
    getGeneralStyles(): Observable<any> {
        return this.generalStylesProvider;
    }

    /**
     * Setter for general styles
     */
    setGeneralStyles(styles: {}): void {
        this.generalStyles = styles;
        this.generalStylesProvider.next(this.generalStyles);
    }

    /**
     * Applies the generalStyles properties to the provided block properties
     */
    applyGeneralStylesToBlock(block: BlockModel) {
        if (!this.blockToGeneralStylePropertiesMap) {
            return;
        }
        const blockPropertyGeneralStyleProperty = this.blockToGeneralStylePropertiesMap.get(block.type.key);

        blockPropertyGeneralStyleProperty.forEach((generalStyleProperty, blockProperty) => {
            const generalStylePropertyValue = this.getPropertyValue(this.generalStyles, generalStyleProperty);
            // Certain style values could be numbers and we want 0 to be an accepted value
            if (generalStylePropertyValue !== null && generalStylePropertyValue !== undefined && block.displayElement.isBoundToGlobalStyles) {
                this.setPropertyValue(block.displayElement, blockProperty, generalStylePropertyValue);
            }
        });

        // Set the template if needed
        if (
            block.displayElement.styles.styleState !== StyleStateEnum.CUSTOM &&
            block.displayElement.styles.styleState !== StyleStateEnum.DEFAULT
        ) {
            const savedStyle = this.styles?.find(style => style.id === block.displayElement.styles.styleState);
            if (savedStyle && block.displayElement.styles.setFromApiModel) {
                const style: GenericStyleModel = savedStyle.clone().style;
                style.styleState = savedStyle.id;
                if (savedStyle.template) {
                    style.template = savedStyle.template;
                }
                block.displayElement.styles.setFromApiModel(style);
            }
        }

        this.notifyBlockDisplayElementChanged(block, true);
    }

    /**
     * Resets the block back to the way it was before editing and closes editing
     */
    cancelChangesToSelectedBlock(): void {
        this.deselectBlock(false);
    }

    /**
     * Saves the edits made to the block and closes editing
     */
    confirmChangesToSelectedBlock(): void {
        this.deselectBlock(true);
    }

    /**
     * Cancels editing on the currently selected block.
     *
     * @param saveChanges True to save hanges, false to reset to values before editing
     */
    deselectBlock(saveChanges: boolean): void {
        if (!saveChanges) {
            this.selectedBlock.displayElement = this.selectedBlockOriginalValues;
            this.formFieldUpdated();
        }

        this.selectedBlockOriginalValues = null;
        this.selectedBlock = null;
        this.selectedBlockProvider.next(this.selectedBlock);
    }

    blockExists(block: BlockModel = this.selectedBlock): boolean {
        if (!block) {
            return false;
        }
        if (this.blocks.indexOf(block) > -1) {
            return true;
        } else {
            let blockFound = false;
            this.blocks.forEach((b, i) => {
                if (!(b as any).blocks) {
                    return;
                }

                if (b instanceof LayoutBlock) {
                    const layoutBlock = b as LayoutBlock;
                    layoutBlock.blocks.forEach(column => {
                        const hasBlock = column.some(x => x.equals(block));
                        if (hasBlock) {
                            blockFound = true;
                        }
                    });
                }

                if (b instanceof CuratorBlock) {
                    b.blocks.forEach(innerBlock => {
                        if (innerBlock.equals(block)) {
                            blockFound = true;
                        }
                    });
                }
            });
            return blockFound;
        }
    }

    /**
     * Handles changes made by the block form and updates the blocks to keep bound relationships in sync
     */
    blockFormUpdated(block: BlockModel, property: string, value: any): void {
        this.formFieldUpdated();
    }

    /**
     * Updates the blocks when changes occur so that things are notified
     */
    formFieldUpdated(): void {
        this.setBlocks(this.blocks);
        this.notifyBlockDisplayElementChanged(this.selectedBlock);
    }

    /**
     * Returns the value of a nested property on the provided object
     */
    getPropertyValue(objectWithProperty: any, property: string): string {
        const parts = property.split('.');
        let prop: any = objectWithProperty;
        for (let i = 0, l = parts.length; i < l; i++) {
            prop = prop[parts[i]];

            if (!prop) {
                break;
            }
        }
        return prop;
    }

    /**
     * Sets nested property on provided object
     */
    setPropertyValue(objectWithProperty: any, property: string, value: any): void {
        const parts = property.split('.');
        let obj: any = objectWithProperty;

        for (let i = 0, l = parts.length; i < l - 1; i++) {
            const propertyToSet = obj[parts[i]];
            if (!propertyToSet) {
                obj[parts[i]] = {};
            }
            obj = obj[parts[i]];
        }
        obj[parts[parts.length - 1]] = value;
    }

    /**
     * Replaces a block with another
     */
    replaceBlock(oldBlock: BlockModel, newBlock: BlockModel): void {
        const blocks = this.blocks;
        const index = blocks.indexOf(oldBlock);
        blocks[index] = newBlock;
        this.setBlocks(this.blocks);
    }

    /**
     * Moves a block in the ordered list
     */
    moveBlock(block: BlockModel, direction: BlockMoveDirectionEnum, position?: string, isToLayoutBlock?: boolean) {
        const blocks = this.blocks;
        const index = blocks.indexOf(block);

        if (index < 0) {
            // shouldn't ever happen
            throw new Error('Item not in list');
        }

        if (direction === BlockMoveDirectionEnum.up) {
            if (index === 0) {
                // already at the top, can't move up more
                return;
            }

            blocks.splice(index, 1);
            blocks.splice(index - 1, 0, block);
        } else if (direction === BlockMoveDirectionEnum.down) {
            if (index === blocks.length - 1) {
                // already at the bottom, can't move down more
                return;
            }

            blocks.splice(index, 1);
            blocks.splice(index + 1, 0, block);
        }

        this.setBlocks(blocks);
    }

    moveBlockToPosition(block: BlockModel, position: string): void {
        return;
    }

    /**
     * Sets the actionability of the blocks
     *
     * @param actionable - true if should be actionable
     */
    setBlocksActionable(actionable: boolean): void {
        this.blocks.forEach(block => {
            block.actionable = actionable;
        });
        this.blocksProvider.next(this.blocks);
    }

    /**
     * Only pushes a value after the templates have been loaded
     */
    hasDefaultTemplates(): Observable<boolean> {
        return this.hasDefaultTemplates$.pipe(filter(x => !!x));
    }

    /**
     * specifies whether layout blocks are allowed
     */
    areLayoutBlocksAllowed(): boolean {
        return false;
    }

    /**
     * Caches a mapping of template type to template
     */
    cacheTemplates(templates: Template[]) {
        templates.forEach(template => {
            if (template.isDefault && template.defaultReference) {
                this.defaultTemplates.set(
                    template.type.toUpperCase() + template.defaultReference.toUpperCase(),
                    template.template
                );
            }
        });

        this.hasDefaultTemplates$.next(true);
    }

    /**
     * Sets the default twig template on each block
     */
    setTemplatesOnBlocks(blocks: BlockModel[]): void {
        blocks?.forEach(block => {
            if (block?.displayElement?.styles?.template?.template) {
                // Expert template case
                block.type.twig = block.displayElement.styles.template.template;
            } else {
                // Default template case
                block.type.twig = this.getDefaultTemplateByBlockType(
                    block.type.key,
                    block.displayElement.styles.getDefaultReference()
                );
            }
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks?.forEach(column => {
                    this.setTemplatesOnBlocks(column);
                });
            }
        });
    }

    /**
     * Returns the default template for the provided block type
     */
    getDefaultTemplateByBlockType(blockType: string, defaultReference: string): string {
        return this.defaultTemplates.get(blockType.toUpperCase() + defaultReference?.toUpperCase());
    }

    /**
     * Returns the block type for the provided style api enum value
     */
    getBlockTypeEnumFromStyleTypeEnum(styleType: string): string {
        return this.styleApiEnumToBlockTypeEnumMap.get(styleType);
    }

    setBlockTypeEnumFromStyleTypeEnumMap(newMap: Map<string, string>) {
        this.styleApiEnumToBlockTypeEnumMap = newMap;
    }

    /**
     * Handles changes to block content style change
     */
    updateContentBlockStyle(block: BlockModel, property: string, value: any): void {
        if (value !== StyleStateEnum.CUSTOM && !isNaN(Number(value))) {
            const defaultStyle = this.getDefaultStyle(block.type.key, value);
            this.setPropertyValue(block.displayElement, 'styles', defaultStyle.clone(block.displayElement.styles));
        }

        if (!this.blockToGeneralStylePropertiesMap) {
            this.formFieldUpdated();
            this.notifyBlockDisplayElementChanged(block, true);
            return;
        }

        if (block.displayElement.isBoundToGlobalStyles) {
            const generalStyleProperty = briefingBlockTypeToGeneralStylesPropertyMap.get(<BriefingBlockTypeEnum>block.type.key);
            this.setPropertyValue(this.generalStyles, generalStyleProperty,  block.displayElement.styles.clone());
            this.updateBoundBlocksOfSameType(block, 'styles', block.displayElement.styles.clone());
        }
        this.formFieldUpdated();
        this.notifyBlockDisplayElementChanged(block, true);
        this.selectBlock(block);
    }

    getDefaultStyle(blockTypeEnum: string, defaultReference?: string | StyleStateEnum): GenericStyleModel {
        if (!defaultReference) {
            defaultReference = StyleStateEnum.DEFAULT;
        }

        const defaultValue = this.defaultStyles[defaultReference][blockTypeEnum].clone();
        defaultValue.displayStyleState = defaultValue.styleState;

        return defaultValue;
    }

    /**
     * Updates all blocks of the same type that are bound to generalStyles
     */
    updateBoundBlocksOfSameType(block: BlockModel, property: string, value: any): void {
        this.blocks.forEach(blockInLoop => {
            this.updateBoundBlockOfSameType(blockInLoop, block, property, value);
            if (blockInLoop.isLayoutBlockType()) {
                (blockInLoop as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        this.updateBoundBlockOfSameType(x, block, property, value);
                    });
                });
            }

            return blockInLoop;
        });
    }

    updateBoundBlockOfSameType(block: BlockModel, sourceBlock: BlockModel, property: string, value: any): void {
        if (!block.type || block.type.key !== sourceBlock.type.key) {
            return;
        }
        const displayElement = block.displayElement;
        if (displayElement.isBoundToGlobalStyles) {
            this.setPropertyValue(displayElement, property, value.clone());
            this.notifyBlockDisplayElementChanged(block, property === 'styles');
        }
    }

    getDefaultStyleSet(defaultReference: StyleStateEnum | string): any {
        return this.defaultStyles[defaultReference];
    }

    renderTemplate(twigTemplate: string, data: any): string {
        return this.twigRenderService.renderTemplate(twigTemplate, data);
    }

    blockEditingStateChanged(block: BlockModel, isEditing: boolean, blockPosition: string) {}

    getBlockEditingState(): Observable<boolean> {
        return this.blockEditingState$;
    }

    setBlockMaxWidth(width: number): void {
        this.blockMaxWidth$.next(width);
    }

    getBlockMaxWidth(): Observable<number> {
        return this.blockMaxWidth$;
    }

    setInlineAdderHeight(height: number): void {
        this.inlineAdderHeight$.next(height);
    }

    getInlineAdderHeight(): Observable<number> {
        return this.inlineAdderHeight$;
    }

    getBlockDisplayElementChanged(): Observable<BlockDisplayElementChangedEvent> {
        return this.blockDisplayElementChanged$;
    }

    notifyBlockDisplayElementChanged(block?: BlockModel, stylesOnly?: boolean): void {
        this.blockDisplayElementChanged$.next({
            block: block || null,
            stylesOnly: stylesOnly
        });
    }

    validateBlock(block: BlockModel, criteria?: BlockValidationTypeEnum[]) {}

    getBlockErrors(blocks: BlockModel[]): any {
        const result = [];
        for (let i = 0; i < blocks.length; i++) {
            const block = blocks[i];
            const type = block.type.key;
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach((column, columnIndex) => {
                    column.forEach((x, positionInColumn) => {
                        if (x.errors?.length) {
                            result.push({
                                id: `block_${i}_${columnIndex}_${positionInColumn}_errors`,
                                label: this.localizationService.transform(
                                    'PageBuilder.layoutBlock.label',
                                    {
                                        index: i + 1,
                                        columnIndex: columnIndex + 1,
                                        positionInColumn: positionInColumn + 1,
                                        type: x.type.key[0] + x.type.key.toLowerCase().slice(1)
                                    }
                                )
                            });
                        }
                    });
                });
            } else if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                if (block.errors?.length) {
                    result.push({
                        id: 'block_' + blocks.indexOf(block) + '_errors',
                        label: this.localizationService.transform(
                            'Pagebuilder.block.label', { index: i + 1, type: this.localizationService.transform(block.type.label)}
                        )
                    });
                }
                (block as CuratorBlock).blocks.forEach((x, index) => {
                    if (x.errors?.length) {
                        result.push({
                            id: `block_${i}_${0}_${index}_errors`,
                            label: this.localizationService.transform(
                                'PageBuilder.layoutBlock.label',
                                {
                                    index: i + 1,
                                    columnIndex: 1,
                                    positionInColumn: index + 1,
                                    type: x.type.key[0] + x.type.key.toLowerCase().slice(1)
                                }
                            )
                        });
                    }
                });
            } else {
                if (block.errors?.length) {
                    result.push({
                        id: 'block_' + blocks.indexOf(block) + '_errors',
                        label: this.localizationService.transform(
                            'Pagebuilder.block.label', { index: i + 1, type: this.localizationService.transform(block.type.label)}
                        )
                    });
                }
            }
        }
        return result;
    }

    setDraggingBlock(block: BlockModel): void {
        this.draggingBlock = block;
        this.draggingBlock$.next(this.draggingBlock);
    }

    getDraggingBlock(): Observable<BlockModel> {
        return this.draggingBlock$;
    }

    setDraggingBlockType(blockType: BlockTypeModel): void {
        this.draggingBlockType = blockType;
        this.draggingBlockType$.next(this.draggingBlockType);
    }

    getDraggingBlockType(): Observable<BlockTypeModel> {
        return this.draggingBlockType$;
    }

    setDraggingBlockPosition(position: string): void {
        this.draggingBlockPosition = position;
        this.draggingBlockPosition$.next(this.draggingBlockPosition);
    }

    getDraggingBlockPosition(): Observable<string> {
        return this.draggingBlockPosition$;
    }

    setFocusedBlockInfo(info: FocusedBlockInfo): void {
        this.focusedBlockInfo = info;
        this.focusedBlockInfo$.next(this.focusedBlockInfo);
    }

    getFocusedBlockInfo(): Observable<FocusedBlockInfo> {
        return this.focusedBlockInfo$;
    }

    setTextBlockToolbarFixedTop(y: number): void {
        this.textBlockToolbarFixedTop = y;
        this.textBlockToolbarFixedTop$.next(this.textBlockToolbarFixedTop);
    }

    getTextBlockToolbarFixedTop(): Observable<number> {
        return this.textBlockToolbarFixedTop$;
    }

    setDisplayChartEntityProfile(displayChartEntityProfile: boolean): void {
        this.displayChartEntityProfile = displayChartEntityProfile;
    }

    getDisplayChartEntityProfile(): boolean {
        return this.displayChartEntityProfile;
    }

    setDisplaySourceAsLink(displaySourceAsLink: boolean): void {
        this.displaySourceAsLink = displaySourceAsLink;
    }

    getDisplaySourceAsLink(): boolean {
        return this.displaySourceAsLink;
    }

    updateChart(block: BlockModel) {}

    updateChildBlockWidths(block: LayoutBlock) {}

    refreshBlockData(blocks: BlockModel) {}

    refreshChartBlockData() {}

    setBlockNoData(x: any): void {}

    getBlockNoData(): Observable<any> {
        return of(null);
    }

    getColumnWidth(blockPosition: string): number {
        return 640;
    }

    getFlattenedIndex(block: BlockModel): number {
        return -1;
    }

    getSaving(): Observable<boolean> {
        return of(false);
    }

    moveOutCuratorBlock(block: BlockModel): void {}

    shouldShowLayoutBlockEmptySectionWarning$(): Observable<boolean> {
        return of(false);
    }

    setIncrementRefreshAllProgressBar(block: BlockModel): void {
        this.incrementRefreshAllProgressBar$.next(block);
    }

    getIncrementRefreshAllProgressBar(): Observable<BlockModel> {
        return this.incrementRefreshAllProgressBar$;
    }
}
